import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, Send, X } from "lucide-react";
import { ask, fetchStatus, type Status, type Turn } from "./askApi";

const SUGGESTED = [
  "What are Applied's Net Zero 2040 goals?",
  "How much did Applied invest in communities in FY 2025?",
  "How is Applied reducing product-use emissions?",
];

function Bubble({ turn, i }: { turn: Turn; i: number }) {
  const mine = turn.role === "user";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`ask-msg-${i}`}>
      <p className={`max-w-[88%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-[15px] leading-relaxed ${mine ? "bg-[var(--am-blue)] text-white" : "bg-[var(--am-mist)] text-[#2b2d30]"}`}>
        {turn.content || <span className="am-dots" aria-label="Thinking" />}
      </p>
    </div>
  );
}

function Suggestions({ onPick }: { onPick: (q: string) => void }) {
  return (
    <div className="space-y-2.5">
      {SUGGESTED.map((q, i) => (
        <button key={q} type="button" onClick={() => onPick(q)} data-testid={`ask-suggestion-${i}`}
          className="block w-full rounded-xl border border-[var(--am-line)] px-4 py-3 text-left text-sm font-semibold text-[var(--am-blue)] transition-colors hover:border-[var(--am-blue)] hover:bg-[var(--am-mist)]">
          {q}
        </button>
      ))}
    </div>
  );
}

function Composer({ disabled, onSend }: { disabled: boolean; onSend: (q: string) => void }) {
  const [q, setQ] = useState("");
  const submit = (e: React.FormEvent) => { e.preventDefault(); if (q.trim() && !disabled) { onSend(q.trim()); setQ(""); } };
  return (
    <form onSubmit={submit} className="flex items-center gap-2">
      <input value={q} onChange={(e) => setQ(e.target.value)} maxLength={500} placeholder="Ask about the 2025 report…" aria-label="Your question" data-testid="ask-input"
        className="min-w-0 flex-1 rounded-full border border-[var(--am-line)] px-4 py-2.5 text-[15px] text-[#2b2d30] outline-none focus:border-[var(--am-blue)]" />
      <button type="submit" disabled={disabled || !q.trim()} aria-label="Send question" data-testid="ask-send"
        className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[var(--am-blue)] text-white transition-opacity disabled:opacity-40">
        <Send size={18} aria-hidden />
      </button>
    </form>
  );
}

export function AskReport() {
  const [status, setStatus] = useState<Status | null>(null);
  const [open, setOpen] = useState(false);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const scroller = useRef<HTMLDivElement>(null);

  useEffect(() => { fetchStatus().then(setStatus); }, []);
  useEffect(() => { scroller.current?.scrollTo({ top: scroller.current.scrollHeight }); }, [turns]);

  if (!status) return null;
  const left = status.remaining;

  const send = (question: string) => {
    if (left <= 0) return;
    const history = turns.filter((t) => t.content);
    setTurns([...history, { role: "user", content: question }, { role: "assistant", content: "" }]);
    setBusy(true); setError("");
    ask(question, history, {
      onText: (t) => setTurns((cur) => { const n = [...cur]; n[n.length - 1] = { role: "assistant", content: n[n.length - 1].content + t }; return n; }),
      onEnd: (remaining, err) => {
        setBusy(false);
        if (typeof remaining === "number") setStatus((s) => (s ? { ...s, remaining } : s));
        if (err) { setError(err); setTurns((cur) => cur.filter((t) => t.content)); }
      },
    });
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.section initial={{ opacity: 0, y: 24, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 16 }} transition={{ duration: 0.25 }}
            className="fixed inset-x-3 bottom-3 z-[60] flex max-h-[78vh] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5 sm:inset-x-auto sm:right-6 sm:bottom-6 sm:w-[400px]"
            aria-label="Ask the report" data-testid="ask-panel">
            <header className="flex items-start justify-between gap-3 bg-[var(--am-blue)] px-5 py-4 text-white">
              <div>
                <h2 className="text-lg font-semibold">Ask the report</h2>
                <p className="mt-0.5 text-sm text-white/90">Answers come only from the 2025 Impact Report.</p>
              </div>
              <button type="button" onClick={() => setOpen(false)} aria-label="Close" data-testid="ask-close" className="rounded-full p-1.5 transition-colors hover:bg-white/15"><X size={20} aria-hidden /></button>
            </header>
            <div ref={scroller} data-lenis-prevent className="flex-1 space-y-3 overflow-y-auto px-4 py-4" data-testid="ask-messages">
              {turns.length === 0 ? <Suggestions onPick={send} /> : turns.map((t, i) => <Bubble key={i} turn={t} i={i} />)}
              {error && <p className="rounded-xl bg-[#fff4d6] px-4 py-2.5 text-sm text-[#5a4300]" role="alert" data-testid="ask-error">{error}</p>}
            </div>
            <footer className="border-t border-[var(--am-line)] px-4 pb-3 pt-3">
              <Composer disabled={busy || left <= 0} onSend={send} />
              <p className="mt-2 text-xs" data-testid="ask-remaining">AI answers can be wrong, so check the full report. {left} question{left === 1 ? "" : "s"} left today.</p>
            </footer>
          </motion.section>
        )}
      </AnimatePresence>
      {!open && (
        <motion.button type="button" onClick={() => setOpen(true)} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} whileHover={{ y: -2 }} data-testid="ask-open"
          className="fixed bottom-5 right-5 z-[60] flex items-center gap-2 rounded-full bg-[var(--am-blue)] px-5 py-3.5 text-base font-semibold text-white shadow-xl sm:bottom-6 sm:right-6">
          <MessageCircle size={20} aria-hidden /> Ask the report
        </motion.button>
      )}
    </>
  );
}
